import * as React from "react";
import { FormProvider } from "react-hook-form";
import { Icon } from "@chakra-ui/react";
import { EmrPuzzle } from "@medstonetech/slate-icons";
import { Loading } from "shared/loading";
import {
  ChartRow,
  ChartRowElement,
  ChartSectionHeader,
  ChartSubsection,
  ChartSubsectionContent,
  ChartSubsectionHeader,
} from "modules/charts-shared/components";
import { useChartForm } from "modules";
import { usePermissions } from "contexts";
import { ChartForm } from "modules/charts-shared-v2";

const sectionId = "SCHC";
const chartCode = "T042x";

function N42ChiefComplaint() {
  const {
    encounterId,
    getIsLoading,
    onSubmit,
    updateIsLoading,
    useFormContext,
    isDirty,
    onRouteChangeSave,
    onSaveAndNext,
  } = useChartForm({
    sectionId,
    sectionLabel: "Chief Complaint",
    chartCode: chartCode,
  });
  const { scope } = usePermissions();
  const isReadOnly = !scope("nurse:cc").isEditable;

  if (getIsLoading) {
    return <Loading />;
  }

  return (
    <FormProvider {...useFormContext}>
      <ChartForm
        onSubmit={onSubmit}
        onRouteChangeSave={onRouteChangeSave}
        isDirty={isDirty}
        isPromptSaveLoading={updateIsLoading}
        disabled={isReadOnly}
        sectionId={sectionId}
      >
        <ChartSectionHeader
          icon={<Icon as={EmrPuzzle} />}
          showNotesBtn={false}
          showActionsBtns
          isLoading={updateIsLoading}
          onSaveAndNext={onSaveAndNext}
        >
          Chief Complaint {isReadOnly ? "(Read Only)" : ""}
        </ChartSectionHeader>

        <ChartSubsection
          header={
            <ChartSubsectionHeader
              showNotesBtn
              chartId={chartCode}
              encounterId={encounterId}
              sectionId={sectionId}
              subsectionId="Q001"
              subsectionLabel="Chief Complaint"
            >
              Chief Complaint
            </ChartSubsectionHeader>
          }
          content={
            <ChartSubsectionContent>
              <ChartRow>
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A001`}
                  type="input"
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="started:"
                  type="cross-option"
                  name={`Q001.${chartCode}${sectionId}Q001A047`}
                />
                <ChartRowElement
                  label="just PTA"
                  name={`Q001.${chartCode}${sectionId}Q001A002`}
                  type="cross-option"
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A003`}
                  w="50px"
                  type="input"
                />
                <ChartRowElement
                  label="minutes ago"
                  name={`Q001.${chartCode}${sectionId}Q001A004`}
                  fontStyle="italic"
                  type="cross-option"
                />
                <ChartRowElement
                  label="hours ago"
                  name={`Q001.${chartCode}${sectionId}Q001A005`}
                  fontStyle="italic"
                  type="cross-option"
                />
                <ChartRowElement
                  label="days ago"
                  name={`Q001.${chartCode}${sectionId}Q001A006`}
                  fontStyle="italic"
                  type="cross-option"
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A007`}
                  type="input"
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="ingested substance:"
                  type="cross-option"
                  name={`Q001.${chartCode}${sectionId}Q001A048`}
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A008`}
                  w="1057px"
                  type="input"
                />
                <ChartRowElement
                  label="time:"
                  type="cross-option"
                  name={`Q001.${chartCode}${sectionId}Q001A049`}
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A009`}
                  type="input"
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="stated quantity:"
                  type="cross-option"
                  name={`Q001.${chartCode}${sectionId}Q001A050`}
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A010`}
                  w="1048px"
                  type="input"
                />
                <ChartRowElement
                  label="vomited x:"
                  type="cross-option"
                  name={`Q001.${chartCode}${sectionId}Q001A051`}
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A011`}
                  type="input"
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="suicide attempt"
                  name={`Q001.${chartCode}${sectionId}Q001A012`}
                  fontStyle="italic"
                  type="cross-option"
                />
                <ChartRowElement
                  label="accidental"
                  name={`Q001.${chartCode}${sectionId}Q001A013`}
                  fontStyle="italic"
                  type="cross-option"
                />
                <ChartRowElement
                  label="other"
                  name={`Q001.${chartCode}${sectionId}Q001A014`}
                  fontStyle="italic"
                  type="cross-option"
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A015`}
                  type="input"
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="smells of:"
                  type="cross-option"
                  name={`Q001.${chartCode}${sectionId}Q001A052`}
                />
                <ChartRowElement
                  label="ETOH"
                  name={`Q001.${chartCode}${sectionId}Q001A016`}
                  fontStyle="italic"
                  type="cross-option"
                />
                <ChartRowElement
                  label="other"
                  name={`Q001.${chartCode}${sectionId}Q001A017`}
                  fontStyle="italic"
                  type="cross-option"
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A018`}
                  type="input"
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="anxiety"
                  name={`Q001.${chartCode}${sectionId}Q001A019`}
                  type="cross-option"
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A020`}
                  type="input"
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="depression"
                  name={`Q001.${chartCode}${sectionId}Q001A021`}
                  type="cross-option"
                />
                <ChartRowElement
                  label="sadness"
                  name={`Q001.${chartCode}${sectionId}Q001A022`}
                  type="cross-option"
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A023`}
                  type="input"
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="seizure activity"
                  name={`Q001.${chartCode}${sectionId}Q001A024`}
                  type="cross-option"
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A025`}
                  type="input"
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="confusion"
                  name={`Q001.${chartCode}${sectionId}Q001A026`}
                  type="cross-option"
                />
                <ChartRowElement
                  label="agitation"
                  name={`Q001.${chartCode}${sectionId}Q001A027`}
                  type="cross-option"
                />
                <ChartRowElement
                  label="delusions"
                  name={`Q001.${chartCode}${sectionId}Q001A028`}
                  type="cross-option"
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A029`}
                  type="input"
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="anger"
                  name={`Q001.${chartCode}${sectionId}Q001A030`}
                  type="cross-option"
                />
                <ChartRowElement
                  label="hostility"
                  name={`Q001.${chartCode}${sectionId}Q001A031`}
                  type="cross-option"
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A032`}
                  type="input"
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="hallucinations:"
                  type="cross-option"
                  name={`Q001.${chartCode}${sectionId}Q001A053`}
                />
                <ChartRowElement
                  label="visual"
                  name={`Q001.${chartCode}${sectionId}Q001A033`}
                  fontStyle="italic"
                  type="cross-option"
                />
                <ChartRowElement
                  label="auditory"
                  name={`Q001.${chartCode}${sectionId}Q001A034`}
                  fontStyle="italic"
                  type="cross-option"
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A035`}
                  type="input"
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="suicidal thoughts"
                  name={`Q001.${chartCode}${sectionId}Q001A036`}
                  type="cross-option"
                />
                <ChartRowElement
                  label="plan:"
                  type="cross-option"
                  name={`Q001.${chartCode}${sectionId}Q001A054`}
                />
                <ChartRowElement
                  label="none"
                  name={`Q001.${chartCode}${sectionId}Q001A037`}
                  fontStyle="italic"
                  type="cross-option"
                />
                <ChartRowElement
                  label="vague"
                  name={`Q001.${chartCode}${sectionId}Q001A038`}
                  fontStyle="italic"
                  type="cross-option"
                />
                <ChartRowElement
                  label="specific"
                  name={`Q001.${chartCode}${sectionId}Q001A039`}
                  fontStyle="italic"
                  type="cross-option"
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A040`}
                  type="input"
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="sleep difficulty"
                  name={`Q001.${chartCode}${sectionId}Q001A041`}
                  type="cross-option"
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A042`}
                  type="input"
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="bizarre behavior"
                  name={`Q001.${chartCode}${sectionId}Q001A043`}
                  type="cross-option"
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A044`}
                  type="input"
                />
              </ChartRow>
              <ChartRow>
                <ChartRowElement
                  label="other"
                  name={`Q001.${chartCode}${sectionId}Q001A045`}
                  type="cross-option"
                />
                <ChartRowElement
                  name={`Q001.${chartCode}${sectionId}Q001A046`}
                  type="input"
                />
              </ChartRow>
            </ChartSubsectionContent>
          }
        />
      </ChartForm>
    </FormProvider>
  );
}

export { N42ChiefComplaint };
